import { useState } from 'react';
import { PackageOpen } from 'lucide-react';
import { computeStockSplit } from '@/lib/inventoryStatus';

interface Props {
  /** Aantal dat voor dit product te bestellen is. */
  needed: number;
  /** Totaal vrij op voorraad (beschikbaar - gereserveerd). */
  free: number;
  busy: boolean;
  onReserve: (qty: number) => void;
}

/**
 * Voorraad-gebruik op het inkoopmoment (FR-7.5/7.6/7.7). Toont hoeveel op voorraad is
 * en laat Mark expliciet kiezen hoeveel hij uit voorraad pakt; de rest wordt besteld.
 * Reserveert de gekozen hoeveelheid. Het automatisch aanpassen van de PO-aantallen is
 * een bewuste vervolgstap — hier toont de tool de split als richtlijn.
 */
export function StockUsePicker({ needed, free, busy, onReserve }: Props) {
  const max = Math.min(free, needed);
  const [qty, setQty] = useState(max);
  if (free <= 0) return null;

  const split = computeStockSplit(qty, needed);

  return (
    <div className="mt-1.5 ml-5 flex flex-wrap items-center gap-2 rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300">
      <PackageOpen className="h-3.5 w-3.5 shrink-0" />
      <span>{free} op voorraad.</span>
      <label className="inline-flex items-center gap-1">
        Gebruik
        <input
          type="number"
          min={0}
          max={max}
          value={qty}
          onChange={(e) => setQty(Math.max(0, Math.min(max, Number(e.target.value))))}
          className="w-14 rounded border border-emerald-300 bg-white px-1.5 py-0.5 text-emerald-900 dark:border-emerald-500/40 dark:bg-bmade-900 dark:text-emerald-200"
        />
      </label>
      <span className="text-emerald-700/80 dark:text-emerald-300/70">
        → {split.useFromStock} uit voorraad, {split.toPurchase} bestellen
      </span>
      <button
        type="button"
        disabled={busy || qty <= 0}
        onClick={() => onReserve(qty)}
        className="rounded bg-emerald-600 px-2 py-0.5 font-medium text-white hover:bg-emerald-700 disabled:opacity-40"
      >
        {busy ? 'Bezig...' : 'Reserveer'}
      </button>
    </div>
  );
}
